/**
 * People-attribution rework S10 (plan §13, D-K/D-J/D-L): the «Traffic into
 * this funnel» rows — a PURE projection of the served
 * `FunnelTrafficSourcesData` (one method behind the popup, the page, the
 * research section and the CLI). No React, no arithmetic on people or money
 * (AC-106): every share, shared spend, CAC and ROAS is read from the row's
 * `funnel` block; the total row is the served scope block, never Σ rows.
 */
import type { FunnelUnit } from './funnelUnit';
import { metaPurchasesCopy, type MetaPurchasesCopy } from './metaPurchasesCopy';
import { cacBesideFromRow, type CacBeside } from './components/PltvWithCac';
import { creativeReasonSelfHealNote, formatMinorMoney, humanizeCreativeReasonCode } from './components/creativeDecisions/creativeDecisionFormat';
import { formatInteger } from './formatters';
import {
  campaignLabel,
  campaignSublabel,
  PEOPLE_REFRESH_REASON,
  type TrafficGateView,
  type TrafficMoneyView,
} from './trafficSourcesRows';
import type {
  FunnelSubscriptionMix,
  FunnelSubscriptionProduct,
  FunnelTrafficCampaignRow,
  FunnelTrafficCreativeRow,
  FunnelTrafficPltvGrain,
  FunnelTrafficSourcesData,
} from './types';

export interface TrafficShareView {
  available: boolean;
  /** 0..1 */
  rate?: number;
  numerator?: number;
  denominator?: number;
  reason?: string;
}

export interface TrafficPltvView {
  /** Projected 12-month LTV per subscriber; undefined = no projection served. */
  value?: number;
  p10?: number;
  p90?: number;
  grain: FunnelTrafficPltvGrain;
  /** Why no projection rides the row — the money-vocabulary phrase of the served withholding code. */
  reason?: string;
  /** The self-heal expectation for that code (tooltip material, not the cell). */
  note?: string;
  /**
   * S12 §15.3: the served watch-only band (snapshot mart, I4 grammar) — the
   * cell's SECOND precedence after a decision projection. Campaign grain has
   * no deploymentKey (tooltip only); creative grain deep-links via it.
   */
  watchOnly?: NonNullable<FunnelTrafficCampaignRow['watchOnlyPltv']> & { deploymentKey?: string };
}

/**
 * S12 §15.4a: the row's subscriptions by BILLING CLASS as the pLTV cohort
 * serves them (`pltv_intervals` on the subscription fact): monthly renewal
 * cycles, upfront (annual/weekly first-payment-only), paid-trial entries,
 * one-time buyers. Only classes with a served count ride the cell; a row that
 * served none is a typed absence. «Weekly trials» (trial × interval) is NOT
 * served at this grain — never derived here.
 */
/**
 * S13 §17.1 item 7: ONE product row of the cell — a COMPUTED label
 * («Annual · $59.99 · 7-day free trial»), its subscriptions and, when the
 * product has trials, the trial→paid line. Ids ride the `title` only.
 */
export interface TrafficProductItem {
  key: string;
  /** «Annual · $59.99 · 7-day free trial» / «Monthly · from $9.99» / «Unknown product». */
  label: string;
  subscriptions: number;
  /** «trial→paid 60 / 92» — only when the product has trials. */
  trialLine?: string;
  /**
   * S13 C1: «412 checks in window · 480 total · 1.9 / sub · active 140 ·
   * cancel scheduled 12»; when in-window == total the window figure is said
   * ONCE («137 checks · 1.0 / sub · active 131»). Absent when the product has
   * no paid cycles or the payload pre-dates C1 — never «0 checks».
   */
  checksLine?: string;
  /** Tooltip: the Stripe ids (+ the price band / mixed-currency note when relevant). */
  title: string;
  /** SI-4: the residual without a price id. */
  unknown?: boolean;
}

export interface TrafficProductsView {
  classes: Array<{ key: 'monthly' | 'weekly' | 'annual' | 'otherRecurring' | 'unknownInterval' | 'upfront' | 'paidTrial' | 'oneTime'; label: string; count: number }>;
  /** S13: one row per product (price_id), sorted by subscriptions desc; undefined = the lane served no list (pre-S13 payload → the classes render). */
  items?: TrafficProductItem[];
  /** DISTINCT subscriptions of the cohort (the «of N subscriptions» sub-line). */
  subscriptions?: number;
  /** S12 §15.4b: the trial line «92 trials: 92 annual · 60 converted · 0 paid» (only when served and > 0). */
  trials?: { started: number; weekly: number; monthly: number; annual: number; paid: number; free: number; converted: number };
  /** 'mix' = the S12 §15.4b select (both grains); 'legacy' = the campaign lane's pLTV cohort fields. */
  source?: 'mix' | 'legacy';
  reason?: string;
}

export interface TrafficSpendEvidence {
  windowSpend?: number | null;
  basis?: 'ad_day' | 'campaign_share';
  exact?: number;
  apportioned?: number;
  campaignShared?: number;
  adDaysExact?: number;
  adDaysShared?: number;
  adDaysUnlinked?: number;
}

/** S12 §15.1b: the served ad × day evidence, read as-is (no arithmetic). */
function spendEvidence(block: FunnelTrafficCampaignRow['funnel']): Omit<TrafficSpendEvidence, 'windowSpend'> {
  if (block.spendBasis !== 'ad_day') return { basis: 'campaign_share' };
  return {
    basis: 'ad_day',
    exact: block.spendExact,
    apportioned: block.spendApportioned,
    campaignShared: block.spendCampaignShared,
    adDaysExact: block.adDaysExact,
    adDaysShared: block.adDaysShared,
    adDaysUnlinked: block.adDaysUnlinked,
  };
}

export interface TrafficRoasView {
  value: number | null | undefined;
  /**
   * 'realized_ltv' = gross-collected realized LTV ÷ shared spend (the column's
   * default, stated once in the header); 'event_revenue' = covered Purchase
   * amounts ÷ shared spend (only when the row has no realized LTV).
   */
  basis?: 'realized_ltv' | 'event_revenue';
  reason?: string;
}

const NO_EVENT_REVENUE_REASON = 'no event revenue · subscriptions bill through the subscription fact';

/**
 * §6 #8 (owner, 2026-09-02): the pLTV ROAS column — served `pltv12Roas` with
 * its P10–P90 band, TONED by the interval bounds against break-even 1× (the
 * only client threshold read; thresholds themselves are OUR policy, named in
 * the column note). Typed absences, never 0×.
 */
/** AC-071 low-n cap: below this subscriber count no success/error tone stands (OUR policy). */
export const PLTV_ROAS_N_FLOOR = 5;

export interface TrafficPltvRoasView {
  value: number | null;
  p10?: number;
  p90?: number;
  /** error = under 1× even at P90 · success = clears 1× from P10 up · warning = band straddles 1×. */
  tone?: 'error' | 'success' | 'warning';
  /** The bound reading («under 1× even at P90»), rendered as the second caption. */
  reading?: string;
  /** The pLTV cohort's subscriptions — n stays visible (AC-071). */
  n?: number;
  lowConfidence?: boolean;
  reason?: string;
}

function pltvRoasView(row: FunnelTrafficCampaignRow): TrafficPltvRoasView {
  if (row.pltvVerdict === 'too_young') return { value: null, reason: 'cohort too young' };
  if (typeof row.pltv12Roas !== 'number') return { value: null, reason: 'no projection served' };
  const view: TrafficPltvRoasView = {
    value: row.pltv12Roas,
    ...(typeof row.pltv12RoasP10 === 'number' ? { p10: row.pltv12RoasP10 } : {}),
    ...(typeof row.pltv12RoasP90 === 'number' ? { p90: row.pltv12RoasP90 } : {}),
    ...(typeof row.pltvSubscriptions === 'number' ? { n: row.pltvSubscriptions } : {}),
    ...(row.pltvLowConfidence ? { lowConfidence: true } : {}),
  };
  if (typeof view.p10 === 'number' && typeof view.p90 === 'number') {
    if (view.p90 < 1) { view.tone = 'error'; view.reading = 'under 1× even at P90'; }
    else if (view.p10 >= 1) { view.tone = 'success'; view.reading = 'clears 1× from P10 up'; }
    else { view.tone = 'warning'; view.reading = 'band straddles 1×'; }
  } else {
    view.reading = 'no band served';
  }
  // Review round 4 (A-P1-5, AC-071 «low-n → warning»): a verdict tone on a
  // handful of subscribers is an overclaim — below the named floor the tone
  // caps at warning and the reading says so (the floor is OUR policy).
  if (typeof view.n === 'number' && view.n < PLTV_ROAS_N_FLOOR && view.tone) {
    view.tone = 'warning';
    view.reading = `${view.reading ?? ''} · n below the ${PLTV_ROAS_N_FLOOR}-sub floor (our policy)`.replace(/^ · /, '');
  }
  return view;
}

/** S11 §14.3 #1: the payback ratio leads; the event ROAS is a fallback, and a structural 0 is a typed absence. */
function roasView(block: { roasCovered: number | null; roasCoveredReason?: 'no_event_revenue'; roasLtvCovered: number | null }, absentReason: string): TrafficRoasView {
  if (typeof block.roasLtvCovered === 'number') return { value: block.roasLtvCovered, basis: 'realized_ltv' };
  if (typeof block.roasCovered === 'number') return { value: block.roasCovered, basis: 'event_revenue' };
  if (block.roasCoveredReason === 'no_event_revenue') return { value: null, reason: NO_EVENT_REVENUE_REASON };
  return { value: null, reason: absentReason };
}

/**
 * S13 §17.1 items 3/9: a ladder gate with the SERVED cost of one person who
 * reached it (`funnel.costPer*`, shared spend ÷ gate people — never derived
 * here, AC-106). `cost` absent = the payload pre-dates S13 (no cost line);
 * `cost.value === null` = a typed dash with its reason.
 */
export type TrafficLadderGateView = TrafficGateView & { cost?: TrafficMoneyView };

/** Phase 2 first item: one product's served seed facts (see FunnelTrafficRow.productFacts). */
export interface TrafficProductFacts {
  priceId: string;
  subscriptions: number;
  checksPerSubscriber?: number | null;
  trialStarted?: number;
  trialConverted?: number;
  ageDaysAvg?: number | null;
  /**
   * Report-mode offer synthesis (owner 2026-09-02): the observed first-paid-
   * cycle price + interval let the calculator BUILD the menu row when the
   * project registry carries no matching product. Verbatim served money —
   * absent under mixed currency (SI-5), never a guess.
   */
  priceMinor?: number | null;
  priceCurrency?: string | null;
  priceExponent?: number | null;
  planInterval?: string | null;
  planIntervalCount?: number | null;
  trialDaysMax?: number | null;
}

export interface FunnelTrafficRow {
  kind: 'campaign' | 'creative';
  id: string;
  label: string;
  sublabel?: string;
  isNoCampaign: boolean;
  identityPlane: 'linkage' | 'behavioural' | undefined;
  share: TrafficShareView;
  /**
   * The version-apportioned spend; `windowSpend` = the source's full window
   * spend (S13: the share block's second line, no longer a column). S12
   * §15.1b: `basis` + parts when the lane served ad × day money.
   */
  spend: TrafficMoneyView & TrafficSpendEvidence;
  people: { value: number | undefined; reason?: string };
  firstToSecond: TrafficLadderGateView;
  paywall: TrafficLadderGateView;
  /**
   * S13 §17.1 item 9: the third ladder gate is the ACCEPTED OFFER (trial OR
   * purchase — `convertedPeople`, the taxonomy's funnel conversion). The
   * paid set is CAC's denominator, kept on `purchase` for other readers.
   */
  offer: TrafficLadderGateView;
  purchase: TrafficGateView;
  costPerStart: TrafficMoneyView;
  cac: CacBeside;
  /**
   * S13 C1: the CAC cell's third line — «BE 6.3 · so far 1.9 / sub», both
   * figures SERVED on the share block (breakEvenChecks / checksPerSubscriber,
   * AC-106). Absent when the payload pre-dates C1 or the server withheld the
   * break-even; the Total row never carries it (people are non-additive).
   */
  cacBreakEven?: string;
  /**
   * Phase 2-min (rowServedPrefill): the leading product's SERVED checks/sub
   * and its Stripe price id, projected verbatim from the share block —
   * `breakEvenChecks` deliberately stays a formatted string only (AC-040:
   * an output-shaped figure must not be re-seedable as an input). Absent =
   * pre-C1 payload.
   */
  checksPerSubscriber?: number | null;
  checksProductKey?: string;
  /**
   * Phase 2 first item: the pressed-buy people count (SERVED `pressedPeople`,
   * §0: price_seen IS pressed) — the p3 seed's numerator over the paywall
   * gate. People unit + campaign rows only; absent on pre-change payloads.
   */
  pressedPeople?: number;
  /**
   * Phase 2 first item: compact per-product SERVED facts for the full-chain
   * seed (accepted mix, checks per EVERY product, trial→paid, cohort age) —
   * verbatim projections of `subscriptionProducts`, nothing derived here.
   */
  productFacts?: TrafficProductFacts[];
  pltv: TrafficPltvView;
  /** ROAS on the shared spend — a ratio, not money; `basis` names the numerator (S11 §14.3 #1). */
  roas: TrafficRoasView;
  /** §6 #8: served pLTV₁₂:CAC ratio, toned by the interval bounds vs 1× — campaign grain only. */
  pltvRoas: TrafficPltvRoasView;
  meta: MetaPurchasesCopy | undefined;
  /** S12 §15.4a: subscriptions by billing class (campaign rows; the ad grain is a typed absence until 15.4b). */
  products: TrafficProductsView;
  campaignLabel?: string;
  /**
   * S13 wave D: the Meta campaign id both kinds share — `metaCampaignId` on a
   * campaign row, `campaignId` on a creative row — so the page can filter
   * creatives by campaign and jump back on the CLIENT (an equality, no
   * request, nothing summed). Absent on behavioural / Google rows.
   */
  campaignId?: string;
}

const PRODUCT_CLASSES: ReadonlyArray<{ key: TrafficProductsView['classes'][number]['key']; label: string; field: keyof FunnelTrafficCampaignRow }> = [
  { key: 'monthly', label: 'monthly', field: 'pltvMonthlySubscriptions' },
  { key: 'upfront', label: 'upfront', field: 'pltvUpfrontSubscriptions' },
  { key: 'paidTrial', label: 'paid trial', field: 'pltvTrialFeeSubscriptions' },
  { key: 'oneTime', label: 'one-time', field: 'pltvOneTimeBuyers' },
];
const NO_PRODUCTS_REASON = 'no subscriptions on this row';
const AD_GRAIN_PRODUCTS_REASON = 'ad grain not served yet';

const MIX_CLASSES: ReadonlyArray<{ key: TrafficProductsView['classes'][number]['key']; label: string; field: keyof FunnelSubscriptionMix }> = [
  { key: 'monthly', label: 'monthly', field: 'monthly' },
  { key: 'weekly', label: 'weekly', field: 'weekly' },
  { key: 'annual', label: 'annual', field: 'annual' },
  { key: 'otherRecurring', label: 'other recurring', field: 'otherRecurring' },
  { key: 'unknownInterval', label: 'unknown interval', field: 'unknownInterval' },
];

/**
 * S12 §15.4b: the served mix (both grains) leads — intervals as classes, the
 * trial entry as its own line; one-time buyers still come from the campaign
 * lane's pLTV cohort field when present. The legacy pLTV fields are the
 * fallback for payloads that pre-date the mix.
 */
function productsFromMix(mix: FunnelSubscriptionMix, oneTime: number | undefined): TrafficProductsView {
  const classes = MIX_CLASSES.flatMap(({ key, label, field }) => (mix[field] > 0 ? [{ key, label, count: mix[field] }] : []));
  if (typeof oneTime === 'number' && oneTime > 0) classes.push({ key: 'oneTime', label: 'one-time', count: oneTime });
  if (classes.length === 0) return { classes, source: 'mix', reason: NO_PRODUCTS_REASON };
  return {
    classes,
    subscriptions: mix.subscriptions,
    source: 'mix',
    ...(mix.trialStarted > 0
      ? { trials: { started: mix.trialStarted, weekly: mix.trialWeekly, monthly: mix.trialMonthly, annual: mix.trialAnnual, paid: mix.paidTrial, free: mix.freeTrial, converted: mix.trialConverted } }
      : {}),
  };
}

const NO_PRODUCTS_ON_VERSION_REASON = 'no subscriptions on this version · projection is campaign-wide';

/**
 * SI-3: an ISO-4217 minor-unit fallback for a served amount whose exponent
 * the lane could not pin (mixed exponent state) — never a hardcoded 2 for
 * every currency.
 */
const ISO_4217_EXPONENTS: Record<string, number> = {
  BIF: 0, CLP: 0, DJF: 0, GNF: 0, JPY: 0, KMF: 0, KRW: 0, MGA: 0, PYG: 0, RWF: 0, UGX: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
};
const exponentFor = (currency: string, served: number | null): number => (
  typeof served === 'number' ? served : (ISO_4217_EXPONENTS[currency.toUpperCase()] ?? 2)
);
const minorMoney = (minor: number, currency: string, exponent: number | null): string => formatMinorMoney(minor, currency, exponentFor(currency, exponent), { narrow: true });

/** «Monthly» / «Annual» / «Weekly» / «Every 3 months» / «Unknown interval» from the Stripe recurring interval. */
export function planIntervalLabel(interval: string | null, count: number | null): string {
  const n = typeof count === 'number' && count > 0 ? count : 1;
  if (!interval) return 'Unknown interval';
  if (n === 1) {
    return interval === 'day' ? 'Daily' : interval === 'week' ? 'Weekly' : interval === 'month' ? 'Monthly' : interval === 'year' ? 'Annual' : interval;
  }
  return `Every ${n} ${interval}s`;
}

/**
 * S13 §17.1 item 7: the computed product label. Price = the first paid
 * cycle band («$59.99», «from $49.99» when MIN ≠ MAX, «mixed currency»,
 * «first cycle not billed yet» when only trials exist — SI-2/SI-5); trial =
 * kind + days + the paid-trial fee when served.
 */
/**
 * S13 C1: the product's paid-checks line — every figure SERVED (AC-106).
 * No line without paid cycles (a trialing-only product is an absence, not
 * «0 checks»); `cancel scheduled` only when > 0; the say-once rule folds
 * «in window · total» into one figure when they are equal.
 */
function productChecksLine(product: FunnelSubscriptionProduct): string | undefined {
  const total = product.paidCyclesTotal;
  if (typeof total !== 'number' || total <= 0) return undefined;
  const inWindow = product.paidCyclesInWindow ?? 0;
  const noun = (n: number): string => (n === 1 ? 'check' : 'checks');
  const head = inWindow === total
    ? [`${formatInteger(total)} ${noun(total)}`]
    : [`${formatInteger(inWindow)} ${noun(inWindow)} in window`, `${formatInteger(total)} total`];
  return [
    ...head,
    ...(typeof product.checksPerSubscriber === 'number' ? [`${product.checksPerSubscriber.toFixed(1)} / sub`] : []),
    ...(typeof product.activeSubscriptions === 'number' ? [`active ${formatInteger(product.activeSubscriptions)}`] : []),
    ...(typeof product.cancelScheduled === 'number' && product.cancelScheduled > 0 ? [`cancel scheduled ${formatInteger(product.cancelScheduled)}`] : []),
  ].join(' · ');
}

/** S13 C1 tooltip material: realized gross / subscriber, refunds, cohort age — served fields only, absent fragments drop. */
function productTooltipExtras(product: FunnelSubscriptionProduct): string[] {
  const extras: string[] = [];
  if (typeof product.grossPerSubscriberMinor === 'number' && product.grossCurrency) {
    extras.push(`realized ${minorMoney(product.grossPerSubscriberMinor, product.grossCurrency, product.grossExponent ?? null)} / subscriber (gross)`);
  }
  if (typeof product.refundedSubscriptions === 'number' && product.refundedSubscriptions > 0) {
    const amount = typeof product.refundedMinor === 'number' && product.refundedMinor > 0 && product.grossCurrency
      ? ` · ${minorMoney(product.refundedMinor, product.grossCurrency, product.grossExponent ?? null)}`
      : '';
    extras.push(`refunded ${formatInteger(product.refundedSubscriptions)}${amount}`);
  }
  if (typeof product.ageDaysAvg === 'number' && typeof product.ageDaysMax === 'number') {
    extras.push(`cohort age avg ${formatInteger(product.ageDaysAvg)} d · oldest ${formatInteger(product.ageDaysMax)} d`);
  }
  return extras;
}

export function productItem(product: FunnelSubscriptionProduct): TrafficProductItem {
  const checksLine = productChecksLine(product);
  const tooltipExtras = productTooltipExtras(product);
  if (product.priceId === null) {
    return {
      key: 'unknown',
      label: 'Unknown product',
      subscriptions: product.subscriptions,
      title: ['no price id on the subscription', ...tooltipExtras].join(' · '),
      unknown: true,
      ...(product.trialStarted > 0 ? { trialLine: `trial→paid ${formatInteger(product.trialConverted)} / ${formatInteger(product.trialStarted)}` } : {}),
      ...(checksLine ? { checksLine } : {}),
    };
  }
  const parts = [planIntervalLabel(product.planInterval, product.planIntervalCount)];
  const notes: string[] = [];
  if (product.pricedSubscriptions > 0 && typeof product.priceMin === 'number' && typeof product.priceMax === 'number') {
    if (product.priceCurrency) {
      parts.push(product.priceMin === product.priceMax
        ? minorMoney(product.priceMin, product.priceCurrency, product.priceExponent)
        : `from ${minorMoney(product.priceMin, product.priceCurrency, product.priceExponent)}`);
      if (product.priceMin !== product.priceMax) notes.push(`first paid cycle ${minorMoney(product.priceMin, product.priceCurrency, product.priceExponent)}–${minorMoney(product.priceMax, product.priceCurrency, product.priceExponent)}`);
    } else {
      parts.push('mixed currency');
    }
  } else if (product.trialStarted > 0) {
    parts.push('first cycle not billed yet');
  }
  if (product.trialStarted > 0) {
    const kind = product.paidTrials > 0 && product.freeTrials === 0 ? 'paid trial' : product.freeTrials > 0 && product.paidTrials === 0 ? 'free trial' : 'trial';
    const days = typeof product.trialDaysMin === 'number' && product.trialDaysMin === product.trialDaysMax ? `${product.trialDaysMin}-day ` : '';
    const fee = product.paidTrials > 0 && product.paidTrialCurrency && typeof product.paidTrialAmountMin === 'number' && product.paidTrialAmountMin === product.paidTrialAmountMax
      ? ` ${minorMoney(product.paidTrialAmountMin, product.paidTrialCurrency, product.paidTrialExponent)}`
      : '';
    parts.push(`${days}${kind}${fee}`);
  }
  return {
    key: product.priceId,
    label: parts.join(' · '),
    subscriptions: product.subscriptions,
    ...(product.trialStarted > 0 ? { trialLine: `trial→paid ${formatInteger(product.trialConverted)} / ${formatInteger(product.trialStarted)}` } : {}),
    ...(checksLine ? { checksLine } : {}),
    title: [`price ${product.priceId}`, product.stripeProductId ? `product ${product.stripeProductId}` : undefined, ...notes, ...tooltipExtras].filter(Boolean).join(' · '),
  };
}

/** S13: the per-product list when served (sorted by n desc, ties stable); the billing-class view stays the fallback. */
function withProductItems(view: TrafficProductsView, products: FunnelSubscriptionProduct[] | undefined): TrafficProductsView {
  if (!products) return view;
  const items = [...products]
    .sort((left, right) => right.subscriptions - left.subscriptions)
    .map(productItem);
  return { ...view, items };
}

function productsView(row: FunnelTrafficCampaignRow): TrafficProductsView {
  if (row.subscriptionMix) return withProductItems(productsFromMix(row.subscriptionMix, row.pltvOneTimeBuyers), row.subscriptionProducts);
  // D-L overlay: the legacy pLTV counts on a 'campaign'-grain row are the
  // CAMPAIGN's cohort, not this version's — never list them as the row's own.
  if (row.funnel.pltvGrain === 'campaign') return { classes: [], source: 'legacy', reason: NO_PRODUCTS_ON_VERSION_REASON };
  const classes = PRODUCT_CLASSES.flatMap(({ key, label, field }) => {
    const count = row[field];
    return typeof count === 'number' && count > 0 ? [{ key, label, count }] : [];
  });
  if (classes.length === 0) return { classes, source: 'legacy', reason: NO_PRODUCTS_REASON };
  return { classes, source: 'legacy', ...(typeof row.pltvSubscriptions === 'number' ? { subscriptions: row.pltvSubscriptions } : {}) };
}

/** S12 §15.4b: the ad-grain twin — the mix when served, else the typed absence. */
function creativeProductsView(creative: FunnelTrafficCreativeRow): TrafficProductsView {
  return creative.subscriptionMix ? withProductItems(productsFromMix(creative.subscriptionMix, undefined), creative.subscriptionProducts) : { classes: [], reason: AD_GRAIN_PRODUCTS_REASON };
}

export interface FunnelTrafficTotalRow {
  label: string;
  spend: TrafficMoneyView;
  people: number;
  firstToSecond: TrafficLadderGateView;
  paywall: TrafficLadderGateView;
  offer: TrafficLadderGateView;
  purchase: TrafficGateView;
  cac: CacBeside;
  overlap: { count: number; noun: string };
}

const NO_GATE_COST_REASON = 'no spend or no one reached this gate';

/**
 * S13: the served gate cost rides the gate. `served === undefined` (pre-S13
 * payload) → no cost line; an unavailable gate never carries a cost.
 */
function ladderGate(view: TrafficGateView, served: number | null | undefined, absent?: TrafficMoneyView): TrafficLadderGateView {
  if (!view.available) return view;
  if (absent) return { ...view, cost: absent };
  if (served === undefined) return view;
  return { ...view, cost: served === null ? { value: null, reason: NO_GATE_COST_REASON } : { value: served } };
}

const NO_AD_ACCOUNT_REASON = 'no ad account';
const NO_VISIT_TWIN_REASON = 'no visit twin for this gate';
const NO_SHARE_REASON = 'no share: source absent from the project-wide run';
const NO_LINKED_JOURNEY_REASON = 'no journey linked to this ad';

const gate = (numerator: number | undefined, denominator: number | undefined, reason = PEOPLE_REFRESH_REASON): TrafficGateView => (
  typeof numerator === 'number' && typeof denominator === 'number'
    ? { available: true, rate: denominator > 0 ? numerator / denominator : null, numerator, denominator }
    : { available: false, reason }
);

function shareView(block: FunnelTrafficCampaignRow['funnel'], versionScoped: boolean, hasSpend: boolean): TrafficShareView {
  if (!versionScoped) return { available: true, rate: 1, numerator: block.shareNumerator, denominator: block.shareDenominator };
  if (block.spendShare === null) {
    return { available: false, reason: hasSpend ? NO_SHARE_REASON : 'no spend to share' };
  }
  return { available: true, rate: block.spendShare, numerator: block.shareNumerator, denominator: block.shareDenominator };
}

/**
 * S11 §14.3 #8: the projection is withheld by a SERVED policy code
 * (`pltvByBasis.gross.unavailableReasons[0]`, e.g. gross cycles unreconciled)
 * far more often than by cohort age — the reason names that code in the
 * money vocabulary (raw snake_case never reaches copy); the blanket
 * «no projection served» is the last resort only.
 */
function pltvAbsence(row: FunnelTrafficCampaignRow): { reason: string; note?: string } {
  if (row.pltvVerdict === 'too_young') return { reason: 'cohort too young' };
  const code = row.pltvByBasis?.gross?.unavailableReasons?.[0];
  const caption = humanizeCreativeReasonCode(code);
  if (!caption) return { reason: 'no projection served' };
  const note = creativeReasonSelfHealNote(code);
  return note ? { reason: caption, note } : { reason: caption };
}

function pltvView(row: FunnelTrafficCampaignRow): TrafficPltvView {
  if (typeof row.pltv12PerSub !== 'number') {
    // S12 §15.3: no decision projection → the watch-only band rides the row
    // (mutual exclusion is structural: exactly one money figure per cell).
    return { grain: row.funnel.pltvGrain, ...pltvAbsence(row), ...(row.watchOnlyPltv ? { watchOnly: row.watchOnlyPltv } : {}) };
  }
  return {
    value: row.pltv12PerSub,
    p10: row.pltv12PerSubP10,
    p90: row.pltv12PerSubP90,
    grain: row.funnel.pltvGrain,
  };
}

function moneyFromShared(value: number | null, reason?: string): TrafficMoneyView {
  return value === null ? { value: null, reason } : { value };
}

/**
 * S13 C1: the short REVIEW form of the break-even line — both figures come
 * SERVED from the share block; a null/absent break-even yields no line at
 * all (never a dash).
 */
export function cacBreakEvenLine(block: { breakEvenChecks?: number | null; checksPerSubscriber?: number | null }): string | undefined {
  if (typeof block.breakEvenChecks !== 'number') return undefined;
  const soFar = typeof block.checksPerSubscriber === 'number' ? ` · so far ${block.checksPerSubscriber.toFixed(1)} / sub` : '';
  return `BE ${block.breakEvenChecks.toFixed(1)}${soFar}`;
}

export function buildFunnelTrafficCampaignRows(data: FunnelTrafficSourcesData, unit: FunnelUnit): FunnelTrafficRow[] {
  const versionScoped = data.scope.versionScoped;
  return data.campaigns.map((row): FunnelTrafficRow => {
    const isNoCampaign = row.campaign === '(not set)';
    const hasAccount = !isNoCampaign && row.peopleIdentityPlane !== 'behavioural' && Boolean(row.metaCampaignId);
    const spend: FunnelTrafficRow['spend'] = isNoCampaign || !hasAccount
      ? { value: null, reason: isNoCampaign ? 'no campaign' : NO_AD_ACCOUNT_REASON }
      : row.spend === null
        ? { value: null, reason: 'spend withheld (mixed currency)' }
        : { ...moneyFromShared(row.funnel.spend, NO_SHARE_REASON), windowSpend: row.spend, ...spendEvidence(row.funnel) };
    // S11 §14.3 #3: the «No campaign» row has no money but DOES have paid
    // people — the count rides the typed absence so the scope denominator
    // (789) is re-derivable from the rows (759 + 30).
    const cac: CacBeside = hasAccount
      ? cacBesideFromRow({ cac: row.funnel.cac, newPayingPeople: row.newPayingPeople, peopleIdentityPlane: row.peopleIdentityPlane })
      : { value: null, reason: isNoCampaign ? 'no campaign' : NO_AD_ACCOUNT_REASON, ...(typeof row.newPayingPeople === 'number' ? { count: row.newPayingPeople } : {}) };
    const common = {
      kind: 'campaign' as const,
      id: row.campaign,
      campaignId: row.metaCampaignId || undefined,
      label: campaignLabel(row),
      sublabel: campaignSublabel(row),
      isNoCampaign,
      identityPlane: row.peopleIdentityPlane,
      share: hasAccount ? shareView(row.funnel, versionScoped, row.spend !== null) : { available: false, reason: spend.reason },
      spend,
      cac,
      // S13 C1: only a row with money carries the break-even line.
      ...(hasAccount && cacBreakEvenLine(row.funnel) ? { cacBreakEven: cacBreakEvenLine(row.funnel) } : {}),
      // Phase 2-min: the leading product's checks facts, served as-is.
      ...(row.funnel.checksPerSubscriber !== undefined ? { checksPerSubscriber: row.funnel.checksPerSubscriber } : {}),
      ...(row.funnel.breakEvenProductKey ? { checksProductKey: row.funnel.breakEvenProductKey } : {}),
      pltv: hasAccount ? pltvView(row) : { grain: 'none' as const, reason: spend.reason },
      roas: hasAccount ? roasView(row.funnel, 'no covered revenue or spend') : { value: null, reason: spend.reason },
      pltvRoas: hasAccount ? pltvRoasView(row) : { value: null, reason: spend.reason },
      meta: hasAccount ? metaPurchasesCopy(row) : undefined,
      products: hasAccount ? productsView(row) : { classes: [], reason: spend.reason },
    };
    // S13: a row without money (no campaign / no ad account) carries the
    // spend's reason under every gate instead of a cost.
    const noMoney: TrafficMoneyView | undefined = hasAccount ? undefined : { value: null, reason: spend.reason };
    if (unit === 'people') {
      return {
        ...common,
        // Phase 2 first item: the pressed count + per-product facts ride the
        // people-unit row only (visits mode has no paywall/pressed twin).
        ...(typeof row.pressedPeople === 'number' ? { pressedPeople: row.pressedPeople } : {}),
        ...(row.subscriptionProducts?.length
          ? {
            productFacts: row.subscriptionProducts
              .filter(product => typeof product.priceId === 'string' && product.priceId.length > 0)
              .map((product): TrafficProductFacts => ({
                priceId: product.priceId as string,
                subscriptions: product.subscriptions,
                ...(product.checksPerSubscriber !== undefined ? { checksPerSubscriber: product.checksPerSubscriber } : {}),
                trialStarted: product.trialStarted,
                trialConverted: product.trialConverted,
                ...(product.ageDaysAvg !== undefined ? { ageDaysAvg: product.ageDaysAvg } : {}),
                // Observed money facts for report-mode offer synthesis.
                ...(product.priceMin !== undefined ? { priceMinor: product.priceMin } : {}),
                ...(product.priceCurrency !== undefined ? { priceCurrency: product.priceCurrency } : {}),
                ...(product.priceExponent !== undefined ? { priceExponent: product.priceExponent } : {}),
                ...(product.planInterval !== undefined ? { planInterval: product.planInterval } : {}),
                ...(product.planIntervalCount !== undefined ? { planIntervalCount: product.planIntervalCount } : {}),
                ...(product.trialDaysMax !== undefined ? { trialDaysMax: product.trialDaysMax } : {}),
              })),
          }
          : {}),
        people: typeof row.startedPeople === 'number' ? { value: row.startedPeople } : { value: undefined, reason: PEOPLE_REFRESH_REASON },
        firstToSecond: ladderGate(gate(row.firstToSecondPeople, row.firstScreenOpenedPeople), row.funnel.costPerFirstToSecond, noMoney),
        paywall: ladderGate(gate(row.paywallReachedPeople, row.startedPeople), row.funnel.costPerPaywall, noMoney),
        offer: ladderGate(gate(row.convertedPeople, row.startedPeople), row.funnel.costPerOffer, noMoney),
        purchase: gate(row.purchasePeople, row.startedPeople),
        costPerStart: hasAccount ? moneyFromShared(row.funnel.costPerStart, 'no started people or spend') : { value: null, reason: spend.reason },
      };
    }
    return {
      ...common,
      people: { value: row.startedJourneys },
      firstToSecond: ladderGate(gate(row.firstToSecondJourneys, row.firstScreenOpenedJourneys, NO_VISIT_TWIN_REASON), row.funnel.costPerFirstToSecond, noMoney),
      paywall: { available: false, reason: NO_VISIT_TWIN_REASON },
      // AC-S13-050: no accepted-offer journey twin on this lane — never backed by purchaseJourneys.
      offer: { available: false, reason: NO_VISIT_TWIN_REASON },
      purchase: gate(row.purchaseJourneys, row.startedJourneys, NO_VISIT_TWIN_REASON),
      costPerStart: hasAccount ? moneyFromShared(row.funnel.costPerStart, 'no started journeys or spend') : { value: null, reason: spend.reason },
    };
  });
}

export function buildFunnelTrafficCreativeRows(data: FunnelTrafficSourcesData, unit: FunnelUnit): FunnelTrafficRow[] {
  const versionScoped = data.scope.versionScoped;
  return data.creatives.map((creative: FunnelTrafficCreativeRow): FunnelTrafficRow => {
    const id = creative.adId ?? creative.creativeId ?? 'unknown';
    const label = creative.creativeName || creative.creativeTitle || creative.creativeId || creative.adId || 'n/a';
    const linked = creative.peopleIdentityPlane === 'linkage';
    const windowSpend = creative.adSpend ?? creative.spend ?? null;
    const absentReason = linked ? PEOPLE_REFRESH_REASON : NO_LINKED_JOURNEY_REASON;
    const common = {
      kind: 'creative' as const,
      id,
      label,
      sublabel: `ad ${creative.adId ?? 'n/a'}`,
      isNoCampaign: false,
      identityPlane: creative.peopleIdentityPlane,
      share: shareView(creative.funnel, versionScoped, windowSpend !== null),
      spend: { ...moneyFromShared(creative.funnel.spend, NO_SHARE_REASON), windowSpend },
      cac: cacBesideFromRow({ cac: creative.funnel.cac, newPayingPeople: creative.newPayingPeople }),
      ...(cacBreakEvenLine(creative.funnel) ? { cacBreakEven: cacBreakEvenLine(creative.funnel) } : {}),
      pltv: { grain: 'none' as const, reason: 'ad grain has no projection', ...(creative.watchOnlyPltv ? { watchOnly: creative.watchOnlyPltv } : {}) },
      roas: roasView(creative.funnel, 'no covered revenue or spend'),
      pltvRoas: { value: null, reason: 'ad grain has no projection' } as TrafficPltvRoasView,
      meta: metaPurchasesCopy(creative),
      products: creativeProductsView(creative),
      campaignLabel: creative.campaignName ?? creative.campaignId,
      campaignId: creative.campaignId || undefined,
    };
    if (unit === 'people') {
      return {
        ...common,
        people: linked && typeof creative.startedPeople === 'number' ? { value: creative.startedPeople } : { value: undefined, reason: absentReason },
        firstToSecond: ladderGate(gate(creative.firstToSecondPeople, creative.firstScreenOpenedPeople, absentReason), creative.funnel.costPerFirstToSecond),
        paywall: ladderGate(gate(creative.paywallReachedPeople, creative.startedPeople, absentReason), creative.funnel.costPerPaywall),
        offer: ladderGate(gate(creative.convertedPeople, creative.startedPeople, absentReason), creative.funnel.costPerOffer),
        purchase: gate(creative.purchasePeople, creative.startedPeople, absentReason),
        costPerStart: moneyFromShared(creative.funnel.costPerStart, absentReason),
      };
    }
    return {
      ...common,
      people: typeof creative.adSessions === 'number' ? { value: creative.adSessions } : { value: undefined, reason: 'sessions did not resolve this ad' },
      firstToSecond: { available: false, reason: NO_VISIT_TWIN_REASON },
      paywall: { available: false, reason: NO_VISIT_TWIN_REASON },
      offer: { available: false, reason: NO_VISIT_TWIN_REASON },
      purchase: gate(creative.adPurchaseJourneys, creative.adSessions, 'sessions did not resolve this ad'),
      costPerStart: moneyFromShared(creative.funnel.costPerStart, 'no sessions or spend'),
    };
  });
}

/** AC-052 / R5: the total row is the SERVED scope block plus the server's Σ shared spend — never Σ rows on the client. */
export function buildFunnelTrafficTotalRow(data: FunnelTrafficSourcesData, unit: FunnelUnit): FunnelTrafficTotalRow | undefined {
  const scope = data.scope.people;
  if (!scope || unit !== 'people') return undefined;
  return {
    label: 'All sources (de-duplicated)',
    spend: data.totals.spend === null ? { value: null, reason: 'no shared spend' } : { value: data.totals.spend },
    people: scope.startedPeople,
    // S13: the scope's ladder costs are SERVED on totals (Σ shared spend ÷
    // scope gate people) — never Σ rows here.
    firstToSecond: ladderGate(gate(scope.firstToSecondPeople, scope.firstScreenOpenedPeople), data.totals.costPerFirstToSecond),
    paywall: ladderGate(gate(scope.paywallReachedPeople, scope.startedPeople), data.totals.costPerPaywall),
    offer: ladderGate(gate(scope.convertedPeople, scope.startedPeople), data.totals.costPerOffer),
    purchase: gate(scope.purchasePeople, scope.startedPeople),
    cac: data.totals.cac === null
      ? { value: null, reason: 'no paid conversions yet', count: scope.newPayingPeople }
      : { value: data.totals.cac, count: scope.newPayingPeople },
    overlap: { count: scope.peopleOnMultipleCampaigns, noun: 'campaign' },
  };
}
